import React, { useEffect, useState } from 'react';
import MainLayout from '../components/MainLayout';
import { RefreshCw, Search, AlertCircle, X } from 'lucide-react';
import { Container, Row, Col, Card, Table, Form, InputGroup, Button, Badge, Spinner } from 'react-bootstrap';
import { optionSellingAPI, OptionSellingMarket } from '../services/optionSellingApi';
import { BROKER_BADGE_VARIANT, BROKER_BADGE_TEXT, BrokerName } from '../services/brokerApi';

// Read-only order history for the Option Selling (manual CE+PE strangle) feature — the raw table
// row itself (leg_role, is_exit_order, batch_id), same convention as NY/SX Spread Orders, so a
// BUY on an already-SOLD strike (closing it out) is never mistaken for a fresh wrong entry the
// way it was when the only place to check was Dhan's own mixed-feature order book.

interface OptionSellingOrder {
  id: number;
  batch_id: string;
  order_id: string;
  order_status: string;
  transaction_type: 'BUY' | 'SELL';
  option_type: 'CE' | 'PE';
  leg_role: 'HEDGE' | 'MAIN';
  trading_symbol: string;
  quantity: number;
  price: number;
  is_exit_order: number | boolean;
  oms_error_description: string | null;
  broker?: string;
  created_at: string;
}

const getStatusVariant = (status: string) => {
  switch (status) {
    case 'TRADED': case 'EXECUTED': return 'success';
    case 'REJECTED': case 'ERROR': return 'danger';
    case 'PENDING': case 'TRANSIT': return 'warning';
    case 'CANCELLED': return 'secondary';
    default: return 'info';
  }
};

const MarketOrderTable: React.FC<{ market: OptionSellingMarket; label: string; accent: string }> = ({ market, label, accent }) => {
  const [orders, setOrders] = useState<OptionSellingOrder[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<OptionSellingOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [brokerFilter, setBrokerFilter] = useState('ALL');
  const [showClearModal, setShowClearModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const fetchOrders = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await optionSellingAPI.getOrderHistory(market, 1, 100);
      setOrders((res.data as any).data || []);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let filtered = [...orders];
    if (searchTerm) filtered = filtered.filter(o => o.trading_symbol?.toLowerCase().includes(searchTerm.toLowerCase()) || o.batch_id?.includes(searchTerm));
    if (statusFilter !== 'ALL') filtered = filtered.filter(o => o.order_status === statusFilter);
    if (typeFilter !== 'ALL') filtered = filtered.filter(o => o.transaction_type === typeFilter);
    if (brokerFilter !== 'ALL') filtered = filtered.filter(o => (o.broker || 'DHAN') === brokerFilter);
    setFilteredOrders(filtered);
  }, [orders, searchTerm, statusFilter, typeFilter, brokerFilter]);

  const stats = {
    total: orders.length,
    traded: orders.filter(o => o.order_status === 'TRADED').length,
    pending: orders.filter(o => o.order_status === 'PENDING' || o.order_status === 'TRANSIT').length,
    rejected: orders.filter(o => o.order_status === 'REJECTED' || o.order_status === 'ERROR').length,
  };

  const handleClearHistory = async () => {
    setShowClearModal(false);
    try {
      await optionSellingAPI.clearOrderHistory(market);
      setOrders([]);
    } catch (err: any) { setError(err.response?.data?.error || err.message); }
  };

  const handleDeletePermanently = async () => {
    setShowDeleteModal(false);
    try {
      await optionSellingAPI.deleteOrdersPermanently(market);
      setOrders([]);
    } catch (err: any) { setError(err.response?.data?.error || err.message); }
  };

  return (
    <div style={{ marginBottom: '24px' }}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 style={{ fontSize: '14px', fontWeight: 700, color: accent, margin: 0 }}>{label} ({market})</h6>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" size="sm" onClick={fetchOrders} disabled={loading}>
            <RefreshCw size={13} className="me-1" />Refresh
          </Button>
          <Button variant="warning" size="sm" onClick={() => setShowClearModal(true)} disabled={orders.length === 0}>
            <X size={13} className="me-1" />Clear History
          </Button>
          <Button variant="danger" size="sm" onClick={() => setShowDeleteModal(true)} disabled={orders.length === 0}>
            <X size={13} className="me-1" />Delete Permanently
          </Button>
        </div>
      </div>

      <Row className="g-2 mb-2">
        {[
          { label: 'Total', value: stats.total, color: '#212529' },
          { label: 'Traded', value: stats.traded, color: '#28a745' },
          { label: 'Pending', value: stats.pending, color: '#ffc107' },
          { label: 'Rejected', value: stats.rejected, color: '#dc3545' },
        ].map(s => (
          <Col md={2} key={s.label}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-2">
                <div className="text-muted" style={{ fontSize: '11px' }}>{s.label}</div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: s.color }}>{s.value}</div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {error && <div className="alert alert-danger py-2" style={{ fontSize: '13px' }}>{error}</div>}

      <Card className="border-0 shadow-sm mb-2">
        <Card.Body className="p-2">
          <Row className="g-2">
            <Col md={4}>
              <InputGroup size="sm">
                <InputGroup.Text><Search size={13} /></InputGroup.Text>
                <Form.Control placeholder="Search symbol or batch ID..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ fontSize: '12px' }} />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select size="sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ fontSize: '12px' }}>
                <option value="ALL">All Status</option>
                <option value="TRADED">Traded</option>
                <option value="TRANSIT">Transit</option>
                <option value="PENDING">Pending</option>
                <option value="REJECTED">Rejected</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select size="sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ fontSize: '12px' }}>
                <option value="ALL">All Types</option>
                <option value="BUY">Buy</option>
                <option value="SELL">Sell</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select size="sm" value={brokerFilter} onChange={e => setBrokerFilter(e.target.value)} style={{ fontSize: '12px' }}>
                <option value="ALL">All Brokers</option>
                <option value="DHAN">Dhan</option>
                <option value="GROWW">Groww</option>
                <option value="ANGEL_ONE">Angel One</option>
                <option value="FYERS">FYERS</option>
                <option value="ALICE_BLUE">Alice Blue</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          <div style={{ overflowX: 'auto' }}>
            <Table hover responsive className="mb-0" style={{ fontSize: '12px' }}>
              <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <tr>
                  <th style={{ padding: '8px', fontSize: '10px', fontWeight: 600 }}>TIME</th>
                  <th style={{ padding: '8px', fontSize: '10px', fontWeight: 600 }}>SYMBOL</th>
                  <th style={{ padding: '8px', fontSize: '10px', fontWeight: 600 }}>LEG</th>
                  <th style={{ padding: '8px', fontSize: '10px', fontWeight: 600 }}>TYPE</th>
                  <th style={{ padding: '8px', fontSize: '10px', fontWeight: 600 }}>ENTRY/EXIT</th>
                  <th style={{ padding: '8px', fontSize: '10px', fontWeight: 600 }}>STATUS</th>
                  <th style={{ padding: '8px', fontSize: '10px', fontWeight: 600, textAlign: 'right' }}>QTY</th>
                  <th style={{ padding: '8px', fontSize: '10px', fontWeight: 600, textAlign: 'right' }}>PRICE</th>
                  <th style={{ padding: '8px', fontSize: '10px', fontWeight: 600 }}>BATCH ID</th>
                  <th style={{ padding: '8px', fontSize: '10px', fontWeight: 600 }}>BROKER</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={10} className="text-center py-4"><Spinner animation="border" size="sm" className="me-2" />Loading...</td></tr>
                ) : filteredOrders.length === 0 ? (
                  <tr><td colSpan={10} className="text-center py-4 text-muted"><AlertCircle size={24} className="mb-2 d-block mx-auto" style={{ opacity: 0.4 }} />No orders found</td></tr>
                ) : (
                  filteredOrders.map(order => (
                    <tr key={order.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '8px', color: '#6c757d', whiteSpace: 'nowrap' }}>
                        {order.created_at ? (
                          <>
                            <div>{new Date(order.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</div>
                            <div style={{ fontSize: '10px' }}>{new Date(order.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</div>
                          </>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '8px', fontWeight: 600 }}>{order.trading_symbol}</td>
                      <td style={{ padding: '8px' }}>
                        <Badge bg={order.leg_role === 'HEDGE' ? 'info' : 'dark'} style={{ fontSize: '10px' }}>{order.leg_role}</Badge>
                      </td>
                      <td style={{ padding: '8px', fontWeight: 600, color: order.transaction_type === 'BUY' ? '#28a745' : '#dc3545' }}>
                        {order.transaction_type}
                      </td>
                      <td style={{ padding: '8px' }}>
                        <span style={{ fontSize: '10px', color: order.is_exit_order ? '#991b1b' : '#065f46', fontWeight: 600 }}>
                          {order.is_exit_order ? 'EXIT' : 'ENTRY'}
                        </span>
                      </td>
                      <td style={{ padding: '8px', maxWidth: '200px' }}>
                        <Badge bg={getStatusVariant(order.order_status)} style={{ fontSize: '10px' }}>{order.order_status}</Badge>
                        {(order.order_status === 'REJECTED' || order.order_status === 'ERROR') && order.oms_error_description && (
                          <div
                            title={order.oms_error_description}
                            style={{
                              fontSize: '10px', color: '#dc3545', marginTop: '3px',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help'
                            }}
                          >
                            {order.oms_error_description}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>{order.quantity}</td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>₹{Number(order.price || 0).toFixed(2)}</td>
                      <td style={{ padding: '8px', color: '#6c757d', fontSize: '10px' }}>{order.batch_id}</td>
                      <td style={{ padding: '8px' }}>
                        <Badge bg={BROKER_BADGE_VARIANT[(order.broker || 'DHAN') as BrokerName]} text={BROKER_BADGE_TEXT[(order.broker || 'DHAN') as BrokerName]} style={{ fontSize: '10px' }}>
                          {order.broker || 'DHAN'}
                        </Badge>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {showClearModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '480px', padding: '24px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#ffc107', marginBottom: '8px' }}>Clear {label} Order History?</h3>
            <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '16px' }}>This will hide {orders.length} {label} Option Selling orders from view (soft delete).</p>
            <div className="d-flex gap-2 justify-content-end">
              <button onClick={() => setShowClearModal(false)} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', background: 'white', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleClearHistory} style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', background: '#ffc107', color: 'white', cursor: 'pointer' }}>Clear History</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '480px', padding: '24px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#dc3545', marginBottom: '8px' }}>Delete {label} Orders Permanently?</h3>
            <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '16px' }}>This will permanently delete {orders.length} {label} Option Selling orders. Cannot be undone!</p>
            <div className="d-flex gap-2 justify-content-end">
              <button onClick={() => setShowDeleteModal(false)} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', background: 'white', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleDeletePermanently} style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', background: '#dc3545', color: 'white', cursor: 'pointer' }}>Delete Permanently</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const OptionSellingOrdersPage: React.FC = () => {
  return (
    <MainLayout>
      <Container fluid className="py-3" style={{ background: '#f8f9fa', minHeight: '100vh' }}>
        <div className="mb-3">
          <h2 className="mb-1" style={{ fontWeight: 700, fontSize: '22px' }}>Option Selling Order History</h2>
          <p className="text-muted mb-0" style={{ fontSize: '13px' }}>
            Every leg (HEDGE/MAIN, ENTRY/EXIT) for every strangle batch — reuses the raw order rows,
            so a BUY closing out an earlier SELL is always labeled EXIT, never mistaken for a fresh entry.
          </p>
        </div>
        <MarketOrderTable market="NIFTY" label="NIFTY" accent="#0d6efd" />
        <MarketOrderTable market="SENSEX" label="SENSEX" accent="#fd7e14" />
      </Container>
    </MainLayout>
  );
};

export default OptionSellingOrdersPage;
